import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { JsonQuestionnaireSection } from '@/components/questionnaires/json-driven/types';
import type { JsonAnswerValue } from '@/components/questionnaires/json-driven/QuestionRenderer';
import { getQuestionKey } from '@/components/questionnaires/json-driven/questionKey';
import { getActiveQuestionCount } from '@/components/questionnaires/json-driven/questionMetrics';
import { getVisibleOrderedQuestions } from '@/components/questionnaires/json-driven/visibleQuestions';
import type { SharedQuestionnaireAssignedClient } from '@/lib/api/questionnaires';
import {
  resolveEffectiveTriggerContext,
  type QuestionnaireDemographicContext,
} from '@/components/questionnaires/json-driven/clientContext';
import {
  evaluateTriggerRules,
  isTriggerQuestion,
  selectedValuesFromAnswer,
} from './evaluate';
import {
  getSectionQuestionnaireIds,
  isPreConsultationScreening,
  normalizeQuestionnaireId,
  setsIntersect,
} from './questionnaireIdentity';
import type {
  TriggerConditionContext,
  TriggerRule,
  TriggerRuleConfig,
  TriggeredQuestionnaireMeta,
} from './types';

export interface TriggeredBlock {
  section: JsonQuestionnaireSection;
  meta: TriggeredQuestionnaireMeta;
}

function buildMeta(
  section: JsonQuestionnaireSection,
  rule: TriggerRule,
  config: TriggerRuleConfig
): TriggeredQuestionnaireMeta {
  const displayTitle = rule.id.startsWith('r-dm-') ? 'Diabetes' : section.title;
  const count = getActiveQuestionCount(section);
  const est =
    section.estimatedTimeMinutes ??
    (config.estimatedTimePerQuestionMinutes ?? 2) * count;
  return {
    questionnaireId: rule.questionnaireId,
    title: displayTitle,
    description: section.description,
    questionCount: count,
    estimatedTimeMinutes: est,
    triggerReason: rule.triggerReason,
  };
}

export interface UseTriggeredQuestionnairesParams {
  section: JsonQuestionnaireSection | null;
  answers: Record<string, JsonAnswerValue>;
  config: TriggerRuleConfig;
  fetchQuestionnaire: (id: string) => Promise<JsonQuestionnaireSection>;
  triggerContext?: TriggerConditionContext;
  demographicContext?: QuestionnaireDemographicContext;
  assignedClient?: SharedQuestionnaireAssignedClient;
}

export interface UseTriggeredQuestionnairesResult {
  /** Trigger question key -> list of triggered blocks (section + meta), ordered, deduped. */
  triggeredByQuestion: Map<string, TriggeredBlock[]>;
  isLoading: boolean;
  error: Error | null;
}

export function useTriggeredQuestionnaires({
  section,
  answers,
  config,
  fetchQuestionnaire,
  triggerContext,
  demographicContext,
  assignedClient,
}: UseTriggeredQuestionnairesParams): UseTriggeredQuestionnairesResult {
  const context = useMemo(
    () =>
      resolveEffectiveTriggerContext(
        answers as Record<string, unknown>,
        assignedClient,
        section,
        triggerContext,
        config.contextFields
      ),
    [answers, assignedClient, section, triggerContext, config.contextFields]
  );
  const currentSectionQuestionnaireIds = useMemo(
    () => getSectionQuestionnaireIds(section),
    [section]
  );

  const { triggerQuestionKeys, rulesByKey, questionnaireIds } = useMemo(() => {
    const keys: string[] = [];
    const byKey = new Map<string, TriggerRule[]>();
    const ids = new Set<string>();

    if (!section || isPreConsultationScreening(section)) {
      return { triggerQuestionKeys: keys, rulesByKey: byKey, questionnaireIds: Array.from(ids) };
    }

    const questions = getVisibleOrderedQuestions(section, answers, demographicContext);
    for (const q of questions) {
      if (!isTriggerQuestion(q, config)) continue;
      const key = getQuestionKey(q);
      const raw = answers[key];
      const selected = selectedValuesFromAnswer(raw);
      const matched = evaluateTriggerRules(selected, context, config).filter((rule) => {
        const ruleQuestionnaireId = normalizeQuestionnaireId(rule.questionnaireId);
        return (
          ruleQuestionnaireId != null &&
          !currentSectionQuestionnaireIds.has(ruleQuestionnaireId)
        );
      });
      if (matched.length === 0) continue;

      if (!keys.includes(key)) keys.push(key);
      const existing = byKey.get(key) ?? [];
      const combined = existing.slice();
      for (const r of matched) {
        if (!combined.some((e) => e.questionnaireId === r.questionnaireId)) {
          combined.push(r);
        }
      }
      byKey.set(key, combined);
      for (const r of matched) ids.add(r.questionnaireId);
    }
    return {
      triggerQuestionKeys: keys,
      rulesByKey: byKey,
      questionnaireIds: Array.from(ids),
    };
  }, [section, answers, config, context, demographicContext, currentSectionQuestionnaireIds]);

  // Stable query list: never pass empty array to useQueries to avoid hook order changes
  const contextCacheKey = `${context.gender ?? ''}:${context.age ?? ''}:${context.pregnant ?? ''}`;

  const queryConfigs = useMemo(() => {
    const list = questionnaireIds.map((id) => ({
      queryKey: ['triggeredQuestionnaire', id, contextCacheKey] as const,
      queryFn: () => fetchQuestionnaire(id),
      enabled: true,
      staleTime: 0,
    }));
    if (list.length === 0) {
      list.push({
        queryKey: ['triggeredQuestionnaire', '__placeholder'] as const,
        queryFn: () => Promise.resolve(null as unknown as JsonQuestionnaireSection),
        enabled: false,
        staleTime: 0,
      });
    }
    return list;
  }, [questionnaireIds, fetchQuestionnaire, contextCacheKey]);

  const queries = useQueries({ queries: queryConfigs });

  const sectionsById = useMemo(() => {
    const m = new Map<string, JsonQuestionnaireSection>();
    for (let i = 0; i < questionnaireIds.length; i++) {
      const d = queries[i]?.data;
      if (d) m.set(questionnaireIds[i], d);
    }
    return m;
  }, [questionnaireIds, queries]);

  const triggeredByQuestion = useMemo(() => {
    const m = new Map<string, TriggeredBlock[]>();
    const activeQuestionnaireIds = new Set(currentSectionQuestionnaireIds);
    for (const qk of triggerQuestionKeys) {
      const rules = rulesByKey.get(qk) ?? [];
      const blocks: TriggeredBlock[] = [];
      for (const r of rules) {
        const sec = sectionsById.get(r.questionnaireId);
        if (!sec) continue;
        const targetQuestionnaireIds = getSectionQuestionnaireIds(sec);
        const ruleQuestionnaireId = normalizeQuestionnaireId(r.questionnaireId);
        if (ruleQuestionnaireId) targetQuestionnaireIds.add(ruleQuestionnaireId);
        if (setsIntersect(targetQuestionnaireIds, activeQuestionnaireIds)) continue;
        for (const id of targetQuestionnaireIds) activeQuestionnaireIds.add(id);
        blocks.push({
          section: sec,
          meta: buildMeta(sec, r, config),
        });
      }
      if (blocks.length) m.set(qk, blocks);
    }
    return m;
  }, [triggerQuestionKeys, rulesByKey, sectionsById, config, currentSectionQuestionnaireIds]);

  const isLoading = queries.some((q) => q.isLoading);
  const err = queries.find((q) => q.error);
  const error = err ? (err.error as Error) : null;

  return {
    triggeredByQuestion,
    isLoading,
    error,
  };
}
